'use client';

import { Box, HStack, Portal, Text } from '@chakra-ui/react';
import { Check, ChevronDown, ListChecks, MessageSquare, Target } from 'lucide-react';
import { Menu } from '@chakra-ui/react';
import type { ResearchExecutionMode } from '@/lib/research-assistant/runtime/run-controller';

const MODE_LABELS: Record<ResearchExecutionMode, string> = {
  standard: 'Standard',
  plan: 'Plan',
  goal: 'Goal',
};

const MODE_DESCRIPTIONS: Record<ResearchExecutionMode, string> = {
  standard: 'Answer and act normally',
  plan: 'Show a plan before execution',
  goal: 'Plan, execute, and verify completion',
};

function ModeIcon({ mode }: { mode: ResearchExecutionMode }) {
  if (mode === 'plan') return <ListChecks size={12} />;
  if (mode === 'goal') return <Target size={12} />;
  return <MessageSquare size={12} />;
}

export function ExecutionModeSelector({
  value,
  modes = ['standard', 'plan', 'goal'],
  disabled,
  onChange,
}: {
  value: ResearchExecutionMode;
  modes?: ResearchExecutionMode[];
  disabled?: boolean;
  onChange: (mode: ResearchExecutionMode) => void;
}) {
  const availableModes = Array.from(new Set(modes));
  if (availableModes.length <= 1) return null;

  return (
    <Menu.Root positioning={{ placement: 'top-start', gutter: 8 }}>
      <Menu.Trigger asChild disabled={disabled}>
        <Box
          as="button"
          data-composer-control-trigger
          display="inline-flex"
          alignItems="center"
          gap="1.5"
          minH="26px"
          px="2"
          borderRadius="full"
          color={value === 'standard' ? 'ui.text.secondary' : 'accent.fg'}
          bg={value === 'standard' ? 'transparent' : 'ui.surface.selected'}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.55 : 1}
          _hover={disabled ? {} : { bg: 'ui.surface.selected', color: 'ui.text.primary' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'accent.solid' }}
          aria-label={`Execution mode: ${MODE_LABELS[value]}`}
          title={`Execution mode: ${MODE_LABELS[value]}`}
        >
          <ModeIcon mode={value} />
          <Text
            as="span"
            data-composer-control-label
            fontSize="xs"
            fontWeight="650"
            whiteSpace="nowrap"
          >
            {MODE_LABELS[value]}
          </Text>
          <ChevronDown data-composer-control-chevron size={12} />
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content layerStyle="surfacePopover" w="284px" p="1.5" borderRadius="2xl">
            {availableModes.map(mode => (
              <Menu.Item
                key={mode}
                value={mode}
                onSelect={() => onChange(mode)}
                minH="48px"
                px="2.5"
                py="1.5"
                borderRadius="glassButton"
                cursor="pointer"
                _hover={{ bg: 'ui.surface.rowHover' }}
              >
                <HStack w="full" gap="2.5">
                  <Box w="3.5" color="accent.fg" flexShrink={0}>
                    {mode === value ? <Check size={14} /> : null}
                  </Box>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight={mode === value ? '700' : '600'}>
                      {MODE_LABELS[mode]}
                    </Text>
                    <Text fontSize="xs" color="ui.text.tertiary">
                      {MODE_DESCRIPTIONS[mode]}
                    </Text>
                  </Box>
                </HStack>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
